import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import TaskIcon from '@mui/icons-material/TaskAltOutlined';
import { useMutation } from '@apollo/client';
import { DELETE_TASK, UPDATE_TASK, UPDATE_TASK_TIMELINE } from '../../graphql/mutations';
import { getApolloErrorMessage } from '../../utils/apolloError';
import { formatPriorityLabel } from '../../utils/projectStatus';
import StatusChip from '../ui/StatusChip';
import { formatDuration, formatTimecode, parseTimecode } from '../../utils/timecode';
import ProTimeline from '../Timeline/ProTimeline';
import type { TimelineItem, TimelineItemChange, TimelineLane } from '../Timeline/timelineTypes';
import {
  WorkspaceTask,
  formatMinutes,
  taskEnd,
  taskStart,
  taskStatusKey,
  taskStatusMeta,
  taskTypeLabel,
} from '../EditorWorkspace/taskMeta';

interface InteractiveTimelineProps {
  project: any;
  /** Tasks can be retimed (drag/resize), edited and deleted. */
  editable: boolean;
  onTasksUpdated?: () => void;
  onSeekTo?: (timestamp: number) => void;
  /** Playhead, when a player drives this timeline. */
  currentTime?: number;
  playing?: boolean;
  /** Media length in seconds; falls back to the project's recorded length. */
  duration?: number;
  fps?: number;
  /** Extra lanes drawn with the tasks (feedback, images, audio). */
  extraLanes?: TimelineLane[];
  /** Where the tasks lane goes relative to the extra lanes. */
  tasksLanePosition?: 'first' | 'after-first';
  title?: React.ReactNode;
  /** Hide the selected-task summary under the timeline. */
  hideDetails?: boolean;
  onTaskSelect?: (taskId: string | null) => void;
}

const MIN_TASK_SECONDS = 0.1;

/**
 * The task timeline on the shared ProTimeline: tasks are bars that can be
 * dragged and resized to retime them (persisted with updateTaskTimeline),
 * double-clicked to edit, and right-clicked to edit or delete.
 */
export default function InteractiveTimeline({
  project,
  editable,
  onTasksUpdated,
  onSeekTo,
  currentTime = 0,
  playing = false,
  duration,
  fps = 30,
  extraLanes = [],
  tasksLanePosition = 'after-first',
  title = 'Timeline',
  hideDetails = false,
  onTaskSelect,
}: InteractiveTimelineProps) {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedOtherId, setSelectedOtherId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ task: WorkspaceTask; x: number; y: number } | null>(null);
  const [editTask, setEditTask] = useState<WorkspaceTask | null>(null);
  // Controlled edit form. The dialog previously used defaultValue on every
  // field and had no onClick on Save, so edits were silently discarded.
  const [editForm, setEditForm] = useState({ title: '', description: '', start: '', end: '', estimatedMinutes: 0 });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // Right-click "Delete Task" asks first (it used to delete immediately).
  const [taskToDelete, setTaskToDelete] = useState<WorkspaceTask | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  // Failures outside a dialog (delete, drag) were only logged.
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const [updateTask] = useMutation(UPDATE_TASK);
  const [updateTaskTimeline] = useMutation(UPDATE_TASK_TIMELINE);
  const [deleteTask] = useMutation(DELETE_TASK, {
    onCompleted: () => onTasksUpdated?.(),
  });

  const projectTasks = project?.tasks;
  const tasks: WorkspaceTask[] = useMemo(
    () => (Array.isArray(projectTasks) ? projectTasks.filter(Boolean) : []),
    [projectTasks]
  );
  const taskById = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);
  const effectiveDuration = duration && duration > 0 ? duration : Number(project?.videoDuration) || 0;

  const tasksLane = useMemo<TimelineLane>(() => ({
    id: 'tasks',
    label: 'Tasks',
    icon: <TaskIcon />,
    kind: 'tasks',
    editable,
    emptyText: 'No tasks with a time range yet',
    items: tasks
      .filter((task) => taskStart(task) !== null)
      .map((task) => {
        const status = taskStatusMeta(task.status);
        const start = taskStart(task) as number;
        const end = taskEnd(task) ?? start + MIN_TASK_SECONDS;
        return {
          id: task.id,
          start,
          end,
          label: task.title,
          color: status.color,
          ariaLabel: `${task.title} (${status.label})`,
          tooltip: (
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>{task.title}</Typography>
              <Typography sx={{ fontSize: 11.5, opacity: 0.8 }}>
                {status.label} · {taskTypeLabel(task.type)}
                {task.estimatedMinutes ? ` · est. ${formatMinutes(task.estimatedMinutes)}` : ''}
              </Typography>
            </Box>
          ),
        } as TimelineItem;
      }),
  }), [tasks, editable]);

  const lanes = useMemo(() => {
    if (extraLanes.length === 0) return [tasksLane];
    if (tasksLanePosition === 'first') return [tasksLane, ...extraLanes];
    return [extraLanes[0], tasksLane, ...extraLanes.slice(1)];
  }, [extraLanes, tasksLane, tasksLanePosition]);

  const selectTask = useCallback((taskId: string | null) => {
    setSelectedTaskId(taskId);
    onTaskSelect?.(taskId);
  }, [onTaskSelect]);

  const openEditDialog = (task: WorkspaceTask) => {
    const start = taskStart(task) ?? 0;
    const end = taskEnd(task) ?? start;
    setEditTask(task);
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      start: formatTimecode(start, fps),
      end: formatTimecode(end, fps),
      estimatedMinutes: task.estimatedMinutes || 0,
    });
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editTask || savingEdit || !editable) return;
    const start = parseTimecode(editForm.start, fps);
    const end = parseTimecode(editForm.end, fps);
    if (start === null || end === null) {
      setEditError('Enter start and end as timecode (HH:MM:SS:FF), m:ss or seconds.');
      return;
    }
    if (end <= start) {
      setEditError('End time must be after start time.');
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    try {
      await updateTask({
        variables: {
          projectId: project?.id,
          taskId: editTask.id,
          updates: {
            title: editForm.title,
            description: editForm.description,
            estimatedMinutes: Math.round(editForm.estimatedMinutes) || 0,
            timeline: { start, end },
          },
        },
      });
      setEditTask(null);
      onTasksUpdated?.();
    } catch (error: any) {
      console.error('Error saving task:', error);
      setEditError(getApolloErrorMessage(error) || 'Could not save this task. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleItemChange = useCallback(async (item: TimelineItem, lane: TimelineLane, next: TimelineItemChange) => {
    if (lane.id !== 'tasks' || !editable) return;
    const end = next.end ?? next.start + MIN_TASK_SECONDS;
    try {
      await updateTaskTimeline({ variables: { taskId: item.id, start: next.start, end } });
      onTasksUpdated?.();
    } catch (error) {
      console.error('Error updating task timeline:', error);
      setTimelineError(`Could not move this task: ${getApolloErrorMessage(error)}`);
      throw error;
    }
  }, [editable, onTasksUpdated, updateTaskTimeline]);

  const confirmDeleteTask = async () => {
    if (!taskToDelete || !editable || deletingTask) return;
    const target = taskToDelete;
    setDeletingTask(true);
    try {
      const result = await deleteTask({ variables: { taskId: target.id } });
      if (result.data?.deleteTask?.success) {
        if (selectedTaskId === target.id) selectTask(null);
      } else {
        setTimelineError(`Could not delete this task: ${result.data?.deleteTask?.message || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      setTimelineError(`Could not delete this task: ${getApolloErrorMessage(error)}`);
    } finally {
      setDeletingTask(false);
      setTaskToDelete(null);
    }
  };

  const selectedTask = selectedTaskId ? taskById.get(selectedTaskId) ?? null : null;
  const editStart = parseTimecode(editForm.start, fps);
  const editEnd = parseTimecode(editForm.end, fps);

  return (
    <>
      <ProTimeline
        title={title}
        duration={effectiveDuration}
        currentTime={currentTime}
        playing={playing}
        fps={fps}
        lanes={lanes}
        selectedItemId={selectedTaskId ?? selectedOtherId}
        onSeek={onSeekTo}
        onItemSelect={(item, lane) => {
          if (lane.id === 'tasks') {
            selectTask(item.id);
            setSelectedOtherId(null);
          } else {
            setSelectedOtherId(item.id);
            selectTask(null);
          }
        }}
        onItemActivate={(item, lane) => {
          const task = lane.id === 'tasks' ? taskById.get(item.id) : undefined;
          if (task && editable) openEditDialog(task);
        }}
        onItemContextMenu={(item, lane, event) => {
          const task = lane.id === 'tasks' ? taskById.get(item.id) : undefined;
          if (!task || !editable) return;
          setContextMenu({ task, x: event.clientX, y: event.clientY });
        }}
        onItemChange={editable ? handleItemChange : undefined}
        toolbarExtras={!editable ? <Chip label="Read only · click to seek" size="small" sx={{ height: 22 }} /> : undefined}
        emptyMessage="Tasks appear here once they have a time range."
        data-testid="task-timeline"
      />

      {!hideDetails && selectedTask && (
        <Paper variant="outlined" sx={{ mt: 1.5, p: 2, borderRadius: 3 }} data-testid="selected-task-details">
          <Typography variant="subtitle2" fontWeight={700}>
            {selectedTask.title}
          </Typography>
          {selectedTask.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedTask.description}
            </Typography>
          )}
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusChip kind="task" status={taskStatusKey(selectedTask.status)} />
            <Typography variant="caption" color="text.secondary">
              {taskTypeLabel(selectedTask.type)} · {formatPriorityLabel(selectedTask.priority)} priority
            </Typography>
            <Typography variant="caption" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {formatTimecode(taskStart(selectedTask) ?? 0, fps)} – {formatTimecode(taskEnd(selectedTask) ?? taskStart(selectedTask) ?? 0, fps)}
              {selectedTask.estimatedMinutes ? ` · ${formatMinutes(selectedTask.estimatedMinutes)} estimated` : ''}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Context menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        <MenuItem
          onClick={() => {
            if (contextMenu) openEditDialog(contextMenu.task);
            setContextMenu(null);
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit task
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (contextMenu && editable) setTaskToDelete(contextMenu.task);
            setContextMenu(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete task
        </MenuItem>
      </Menu>

      {/* Edit Task Dialog */}
      <Dialog open={Boolean(editTask)} onClose={() => !savingEdit && setEditTask(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit task</DialogTitle>
        <DialogContent>
          {editTask && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {editError && <Alert severity="error">{editError}</Alert>}
              <TextField
                label="Title"
                value={editForm.title}
                onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start"
                  value={editForm.start}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, start: e.target.value }))}
                  helperText={editStart === null ? 'HH:MM:SS:FF, m:ss or seconds' : `${editStart.toFixed(2)}s`}
                  error={editStart === null}
                  inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="End"
                  value={editForm.end}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, end: e.target.value }))}
                  helperText={
                    editEnd === null
                      ? 'HH:MM:SS:FF, m:ss or seconds'
                      : editStart !== null && editEnd > editStart
                        ? `Duration ${formatDuration(editEnd - editStart)}`
                        : `${editEnd.toFixed(2)}s`
                  }
                  error={editEnd === null}
                  inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
                  sx={{ flex: 1 }}
                />
              </Box>
              <TextField
                label="Estimated minutes"
                type="number"
                value={editForm.estimatedMinutes}
                onChange={(e) => setEditForm((prev) => ({ ...prev, estimatedMinutes: parseInt(e.target.value, 10) || 0 }))}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditTask(null)} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit || !editable}>
            {savingEdit ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (same wording as the task cards' dialog) */}
      <Dialog open={Boolean(taskToDelete)} onClose={() => { if (!deletingTask) setTaskToDelete(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this task?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {taskToDelete?.title ? `“${taskToDelete.title}” and your notes on it are removed. ` : ''}This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTaskToDelete(null)} disabled={deletingTask}>Cancel</Button>
          <Button onClick={confirmDeleteTask} color="error" variant="contained" disabled={deletingTask || !editable}>
            {deletingTask ? 'Deleting…' : 'Delete task'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!timelineError}
        autoHideDuration={6000}
        onClose={() => setTimelineError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setTimelineError(null)} severity="error" sx={{ width: '100%' }}>
          {timelineError}
        </Alert>
      </Snackbar>
    </>
  );
}
